import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search,
  Save,
  X,
  Trash2,
  RefreshCw,
  Download,
  Store,
  AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuppliersTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  const [suppliers, setSuppliers] = useState([]);
  const [originalSuppliers, setOriginalSuppliers] = useState([]);
  const [modifiedIds, setModifiedIds] = useState(new Set());
  
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Fetch suppliers
  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/suppliers`, {
        params: { sort: sortField, order: sortOrder }
      });
      setSuppliers(response.data.suppliers);
      setOriginalSuppliers(JSON.parse(JSON.stringify(response.data.suppliers)));
      setPaymentMethods(response.data.payment_methods);
      setPaymentTerms(response.data.payment_terms);
      setModifiedIds(new Set());
    } catch (err) {
      console.error("Error fetching suppliers:", err);
      toast.error("Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  }, [sortField, sortOrder]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Filter suppliers by search
  const filteredSuppliers = useMemo(() => {
    if (!searchQuery) return suppliers;
    const query = searchQuery.toLowerCase();
    return suppliers.filter(s => 
      s.name.toLowerCase().includes(query) ||
      (s.payment_method && s.payment_method.toLowerCase().includes(query)) ||
      (s.payment_term && s.payment_term.toLowerCase().includes(query))
    );
  }, [suppliers, searchQuery]);

  // Check for changes
  const hasChanges = modifiedIds.size > 0;

  // Handle field change
  const handleFieldChange = (supplierId, field, value) => {
    setSuppliers(prev => prev.map(s => {
      if (s.id === supplierId) {
        return { ...s, [field]: value };
      }
      return s;
    }));
    setModifiedIds(prev => new Set([...prev, supplierId]));
  };

  // Sync from invoices
  const handleSyncFromInvoices = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/suppliers/bulk-from-invoices`);
      toast.success(`Synced: ${response.data.created} new, ${response.data.skipped} already existed`);
      await fetchSuppliers();
    } catch (err) {
      console.error("Error syncing suppliers:", err);
      toast.error("Failed to sync suppliers");
    } finally {
      setSyncing(false);
    }
  };

  // Save changes
  const handleSave = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    try {
      const updates = suppliers
        .filter(s => modifiedIds.has(s.id))
        .map(s => ({
          id: s.id,
          payment_method: s.payment_method,
          payment_term: s.payment_term
        }));
      
      await axios.patch(`${API}/suppliers/bulk`, { updates });
      
      setOriginalSuppliers(JSON.parse(JSON.stringify(suppliers)));
      setModifiedIds(new Set());
      toast.success("Suppliers saved successfully");
    } catch (err) {
      console.error("Error saving suppliers:", err);
      toast.error("Failed to save suppliers");
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setSuppliers(JSON.parse(JSON.stringify(originalSuppliers)));
    setModifiedIds(new Set());
    toast.info("Changes discarded");
  };

  // Delete supplier
  const handleDelete = async (supplier) => {
    try {
      const response = await axios.delete(`${API}/suppliers/${supplier.id}`);
      if (response.data.warning) {
        toast.warning(response.data.warning);
      }
      toast.success("Supplier deleted");
      setSuppliers(prev => prev.filter(s => s.id !== supplier.id));
      setOriginalSuppliers(prev => prev.filter(s => s.id !== supplier.id));
      setModifiedIds(prev => {
        const next = new Set(prev);
        next.delete(supplier.id);
        return next;
      });
    } catch (err) {
      console.error("Error deleting supplier:", err);
      toast.error("Failed to delete supplier");
    }
    setDeleteConfirm(null);
  };

  // Export CSV
  const handleExportCSV = () => {
    const headers = ["ID", "Name", "Payment Method", "Payment Term"];
    const rows = suppliers.map(s => [
      s.id,
      `"${s.name}"`,
      s.payment_method || "",
      s.payment_term || ""
    ]);
    
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "suppliers.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  // Toggle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-state">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="suppliers-tab">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search suppliers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncFromInvoices}
            disabled={syncing}
            data-testid="sync-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync from Invoices
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            disabled={suppliers.length === 0}
            data-testid="export-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Suppliers Table */}
      {suppliers.length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center" data-testid="empty-state">
          <Store className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">No suppliers</h3>
          <p className="text-muted-foreground mb-4">
            Import purchase invoices to automatically add suppliers, 
            or click "Sync from Invoices" to synchronize existing ones.
          </p>
        </div>
      ) : (
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="suppliers-table">
              <thead className="bg-muted/30 border-b">
                <tr>
                  <th 
                    className="text-left px-4 py-3 font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort("name")}
                  >
                    Supplier {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th 
                    className="text-left px-4 py-3 font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 w-[180px]"
                    onClick={() => handleSort("payment_method")}
                  >
                    Payment Method {sortField === "payment_method" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th 
                    className="text-left px-4 py-3 font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 w-[180px]"
                    onClick={() => handleSort("payment_term")}
                  >
                    Payment Term {sortField === "payment_term" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="w-[60px]"></th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.map((supplier, index) => {
                  const isModified = modifiedIds.has(supplier.id);
                  
                  return (
                    <tr 
                      key={supplier.id}
                      className={`border-b last:border-b-0 transition-colors hover:bg-muted/20 ${index % 2 === 0 ? 'bg-white' : 'bg-muted/5'}`}
                      data-testid={`supplier-row-${supplier.id}`}
                    >
                      <td className="px-4 py-2">
                        <span className="font-medium">{supplier.name}</span>
                        {isModified && (
                          <span className="ml-2 inline-block w-2 h-2 rounded-full bg-amber-500" title="Unsaved changes" />
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Select
                          value={supplier.payment_method || ""}
                          onValueChange={(val) => handleFieldChange(supplier.id, "payment_method", val)}
                        >
                          <SelectTrigger 
                            className={`h-8 text-sm ${!supplier.payment_method ? 'text-muted-foreground italic' : ''} ${isModified ? 'border-amber-400' : ''}`}
                            data-testid={`payment-method-${supplier.id}`}
                          >
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {paymentMethods.map(method => (
                              <SelectItem key={method} value={method}>
                                {method}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-2">
                        <Select
                          value={supplier.payment_term || ""}
                          onValueChange={(val) => handleFieldChange(supplier.id, "payment_term", val)}
                        >
                          <SelectTrigger 
                            className={`h-8 text-sm ${!supplier.payment_term ? 'text-muted-foreground italic' : ''} ${isModified ? 'border-amber-400' : ''}`}
                            data-testid={`payment-term-${supplier.id}`}
                          >
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {paymentTerms.map(term => (
                              <SelectItem key={term} value={term}>
                                {term}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirm(supplier)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          data-testid={`delete-${supplier.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Table Footer */}
          <div className="px-4 py-3 bg-muted/20 border-t flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {filteredSuppliers.length} of {suppliers.length} suppliers
              {modifiedIds.size > 0 && ` • ${modifiedIds.size} modified`}
            </span>
          </div>
        </div>
      )}

      {/* Floating Action Bar */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white border rounded-full shadow-lg px-6 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4" data-testid="action-bar">
          <span className="text-sm text-muted-foreground">
            {modifiedIds.size} changes
          </span>
          <Button variant="outline" onClick={handleDiscard} className="rounded-full" data-testid="discard-button">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-button">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete supplier?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SuppliersTab;
